import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Tag, Typography, message } from 'antd';
import { SlidersOutlined } from '@ant-design/icons';
import API_BASE_URL from '../../helpers/api/apiConfig';
import MetricRuleEditorModal from '../MetricRules/MetricRuleEditorModal';
import {
  MetricRuleSet,
  buildConfigurationsWithAreaTypeMetricRule,
  getAreaTypeMetricRuleSetForKey,
  getMetricRuleDefaultsFromMetric,
  isMetricRuleSetEmpty,
} from '../../helpers/metricRules';

const { Text } = Typography;

interface BackOfficeRangesDefaultsTabProps {
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

interface AreaTypeOption {
  id: number;
  name: string;
}

interface CompanyOption {
  id: number;
  name: string;
  address?: string | null;
}

const BackOfficeRangesDefaultsTab: React.FC<BackOfficeRangesDefaultsTabProps> = ({
  buildAuthHeaders,
  onAuthMismatch,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingCompanyId, setApplyingCompanyId] = useState<number | null>(null);
  const [defaultsConfigurations, setDefaultsConfigurations] = useState<Record<string, any>>({});
  const [hasStoredDefaults, setHasStoredDefaults] = useState(false);
  const [areaTypes, setAreaTypes] = useState<AreaTypeOption[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedAreaTypeId, setSelectedAreaTypeId] = useState<number | null>(null);
  const [editingMetric, setEditingMetric] = useState<any | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [defaultsRes, areaTypesRes, metricsRes, companyLicensesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/back-office/range-defaults`, {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/area-types?is_global=true`, {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/metrics?active=true`, {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/company-licenses`, {
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
        }),
      ]);

      if (!defaultsRes.ok) {
        const errorData = await defaultsRes.json();
        if (await onAuthMismatch(defaultsRes, errorData)) return;
        message.error(errorData?.message || errorData?.error || 'Failed to load range defaults');
        return;
      }
      if (!areaTypesRes.ok) {
        const errorData = await areaTypesRes.json();
        if (await onAuthMismatch(areaTypesRes, errorData)) return;
        message.error(errorData?.message || errorData?.error || 'Failed to load area types');
        return;
      }
      if (!metricsRes.ok) {
        const errorData = await metricsRes.json();
        if (await onAuthMismatch(metricsRes, errorData)) return;
        message.error(errorData?.message || errorData?.error || 'Failed to load metrics');
        return;
      }
      if (!companyLicensesRes.ok) {
        const errorData = await companyLicensesRes.json();
        if (await onAuthMismatch(companyLicensesRes, errorData)) return;
        message.error(errorData?.message || errorData?.error || 'Failed to load companies');
        return;
      }

      const defaultsDataRaw = await defaultsRes.json();
      const areaTypesDataRaw = await areaTypesRes.json();
      const metricsDataRaw = await metricsRes.json();
      const companyLicensesDataRaw = await companyLicensesRes.json();

      const nextAreaTypes: AreaTypeOption[] = (Array.isArray(areaTypesDataRaw?.data)
        ? areaTypesDataRaw.data
        : []
      )
        .map((item: any) => ({
          id: Number(item?.id),
          name: String(item?.name || '').trim(),
        }))
        .filter(
          (item: AreaTypeOption) => Number.isFinite(item.id) && item.id > 0 && item.name.length > 0
        )
        .sort((a: AreaTypeOption, b: AreaTypeOption) => a.name.localeCompare(b.name));

      const nextMetrics = (Array.isArray(metricsDataRaw?.data) ? metricsDataRaw.data : [])
        .filter((metric: any) => Boolean(metric?.key))
        .sort((a: any, b: any) => String(a?.name || a?.key).localeCompare(String(b?.name || b?.key)));

      const nextCompaniesById = new Map<number, CompanyOption>();
      const companyLicenses = Array.isArray(companyLicensesDataRaw?.data)
        ? companyLicensesDataRaw.data
        : [];
      companyLicenses.forEach((license: any) => {
        const companyId = Number(license?.company?.id ?? license?.companyId);
        const companyName = String(license?.company?.name || '').trim();
        if (!Number.isFinite(companyId) || companyId <= 0 || !companyName) return;
        if (!nextCompaniesById.has(companyId)) {
          nextCompaniesById.set(companyId, {
            id: companyId,
            name: companyName,
            address: license?.company?.address ?? null,
          });
        }
      });

      const nextCompanies: CompanyOption[] = Array.from(nextCompaniesById.values())
        .filter(
          (company: CompanyOption) => Number.isFinite(company.id) && company.id > 0 && company.name.length > 0
        )
        .sort((a: CompanyOption, b: CompanyOption) => a.name.localeCompare(b.name));

      setDefaultsConfigurations(defaultsDataRaw?.data?.configurations || {});
      setHasStoredDefaults(Boolean(defaultsDataRaw?.data?.hasStoredDefaults));
      setAreaTypes(nextAreaTypes);
      setMetrics(nextMetrics);
      setCompanies(nextCompanies);
      setSelectedAreaTypeId((current) => {
        if (current && nextAreaTypes.some((areaType) => areaType.id === current)) {
          return current;
        }
        return nextAreaTypes.length > 0 ? nextAreaTypes[0].id : null;
      });
    } catch (error) {
      console.error('Failed to load back-office range defaults view', error);
      message.error('Failed to load range defaults');
    } finally {
      setLoading(false);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const persistDefaultsConfigurations = useCallback(async (nextConfigurations: Record<string, any>) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/back-office/range-defaults`, {
        method: 'PUT',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ configurations: nextConfigurations }),
      });

      if (response.ok) {
        const data = await response.json();
        setDefaultsConfigurations(data?.data?.configurations || nextConfigurations);
        setHasStoredDefaults(true);
        message.success('Back Office defaults updated');
        return true;
      }

      const errorData = await response.json();
      if (await onAuthMismatch(response, errorData)) return false;
      message.error(errorData?.message || errorData?.error || 'Failed to update defaults');
      return false;
    } catch (error) {
      console.error('Failed to persist defaults', error);
      message.error('Failed to update defaults');
      return false;
    } finally {
      setSaving(false);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  const handleSave = async (nextRuleSet: MetricRuleSet | null) => {
    if (!editingMetric || !selectedAreaTypeId) return;

    const nextConfigurations = buildConfigurationsWithAreaTypeMetricRule(
      defaultsConfigurations || {},
      selectedAreaTypeId,
      editingMetric.key,
      nextRuleSet
    );

    const ok = await persistDefaultsConfigurations(nextConfigurations);
    if (!ok) return;
    setEditorOpen(false);
    setEditingMetric(null);
  };

  const applyDefaultsToCompany = useCallback(async (companyId: number) => {
    setApplyingCompanyId(companyId);
    try {
      const response = await fetch(`${API_BASE_URL}/back-office/range-defaults/apply/company/${companyId}`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const appliedCount = Number(data?.data?.appliedMetricRuleCount || 0);
        message.success(appliedCount > 0
          ? `Applied ${appliedCount} default metric rules`
          : 'No new defaults were needed');
        return;
      }

      const errorData = await response.json();
      if (await onAuthMismatch(response, errorData)) return;
      message.error(errorData?.message || errorData?.error || 'Failed to apply defaults');
    } catch (error) {
      console.error('Failed to apply defaults to company', error);
      message.error('Failed to apply defaults');
    } finally {
      setApplyingCompanyId(null);
    }
  }, [buildAuthHeaders, onAuthMismatch]);

  const editingRuleSet = useMemo(() => {
    if (!editingMetric || !selectedAreaTypeId) return null;
    const current = getAreaTypeMetricRuleSetForKey(
      defaultsConfigurations,
      selectedAreaTypeId,
      editingMetric.key
    );
    if (current && !isMetricRuleSetEmpty(current)) {
      return current;
    }

    return null;
  }, [defaultsConfigurations, editingMetric, selectedAreaTypeId]);

  const editingDefaults = useMemo(() => {
    if (!editingMetric) return null;
    return getMetricRuleDefaultsFromMetric(editingMetric);
  }, [editingMetric]);

  const metricRuleSummary = useCallback((metricKey: string) => {
    if (!selectedAreaTypeId) return [];
    const current = getAreaTypeMetricRuleSetForKey(
      defaultsConfigurations,
      selectedAreaTypeId,
      metricKey
    );
    const tags: string[] = [];

    if (current?.warningRange && Object.keys(current.warningRange).length > 0) tags.push('warning');
    if (current?.colorRange && Object.keys(current.colorRange).length > 0) tags.push('color');
    if (current?.notificationRange && Object.keys(current.notificationRange).length > 0) tags.push('notification');
    if (current?.healthStatusRange && Object.keys(current.healthStatusRange).length > 0) tags.push('health');
    if (current?.benchmarkRange && Object.keys(current.benchmarkRange).length > 0) tags.push('benchmark');
    if (current?.notificationLogic) tags.push('notification logic');
    if (current?.healthStatusLogic) tags.push('health logic');

    return tags;
  }, [defaultsConfigurations, selectedAreaTypeId]);

  const metricColumns = useMemo(() => [
    {
      title: 'Metric',
      dataIndex: 'name',
      key: 'name',
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name || record.key}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.key}</Text>
        </Space>
      ),
    },
    {
      title: 'Default Rules',
      key: 'defaults',
      render: (_: any, record: any) => {
        const tags = metricRuleSummary(record.key);
        if (tags.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space wrap>
            {tags.map((label) => (
              <Tag key={`${record.key}-${label}`}>{label}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button disabled={!selectedAreaTypeId} onClick={() => {
          setEditingMetric(record);
          setEditorOpen(true);
        }}>
          Edit
        </Button>
      ),
    },
  ], [metricRuleSummary, selectedAreaTypeId]);

  const companyColumns = useMemo(() => [
    {
      title: 'Company ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (value: number) => <Text code>{value}</Text>,
    },
    {
      title: 'Company',
      dataIndex: 'name',
      key: 'name',
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: any, record: CompanyOption) => (
        <Button
          loading={applyingCompanyId === record.id}
          onClick={() => applyDefaultsToCompany(record.id)}
        >
          Apply defaults
        </Button>
      ),
    },
  ], [applyDefaultsToCompany, applyingCompanyId]);

  if (areaTypes.length === 0 && !loading) {
    return (
      <Alert
        message="No Global Area Types"
        description="Create at least one global area type to define reusable metric defaults."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card
        title={
          <Space>
            <SlidersOutlined />
            Range Defaults
          </Space>
        }
        loading={loading}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type={hasStoredDefaults ? 'success' : 'warning'}
            showIcon
            message={hasStoredDefaults
              ? 'Back Office defaults are configured. Use "Apply defaults" to push them to companies.'
              : 'No Back Office defaults configured yet. Set up ranges for each metric and area type here, then use "Apply defaults" to push them to companies. New companies will start with empty configurations until defaults are saved.'}
          />

          <div>
            <Text strong>Area Type</Text>
            <div style={{ marginTop: 8, maxWidth: 420 }}>
              <Select
                value={selectedAreaTypeId ?? undefined}
                onChange={(value) => setSelectedAreaTypeId(value)}
                style={{ width: '100%' }}
                placeholder="Select area type"
                options={areaTypes.map((areaType) => ({
                  label: areaType.name,
                  value: areaType.id,
                }))}
              />
            </div>
          </div>

          <Table
            rowKey={(record) => record.id || record.key}
            columns={metricColumns}
            dataSource={metrics}
            pagination={{ pageSize: 8 }}
            locale={{ emptyText: 'No metrics found.' }}
          />
        </Space>
      </Card>

      <Card title="Apply Defaults to Companies" loading={loading}>
        <Table
          rowKey="id"
          columns={companyColumns}
          dataSource={companies}
          pagination={{ pageSize: 8 }}
          locale={{ emptyText: 'No companies found.' }}
        />
      </Card>

      <MetricRuleEditorModal
        open={editorOpen}
        metric={editingMetric}
        initialRuleSet={editingRuleSet}
        defaults={editingDefaults}
        saving={saving}
        onCancel={() => {
          setEditorOpen(false);
          setEditingMetric(null);
        }}
        onSave={handleSave}
      />
    </Space>
  );
};

export default BackOfficeRangesDefaultsTab;
